import {useEffect, useState} from 'react';
import {cloneDeep, isEqual} from 'lodash-es';
import {router} from '@/router';
import {useRosterAwareState} from '@/hooks/useRosterAwareState';
import {useRosterStore} from '@/store/roster_store';
import {FilterInvalidate} from '@common/FilterComponent';
import Helpers from '@/helpers/helpers';

export type QueryFnCallback = (forceInvalidate?: boolean) => boolean;

type Options<T> = {
  invalidate: FilterInvalidate;
  params: T;
  // this is especially useful inside TabsComponent with route and forceRender
  multipleRoutesRendered?: boolean,
  invalidateOnUnMount?: boolean,
}

export function useFilterQuery<T extends object>(
  { invalidate, params, multipleRoutesRendered, invalidateOnUnMount }: Options<T>) {
  const currentRoster = useRosterStore((state) => state.currentRoster);
  const currentRosterId = currentRoster?.id ?? 0;

  const [currentRosterParams] = useRosterAwareState(Object.fromEntries(
    Object.entries(cloneDeep(params)).map(([key]) => [key, []]),
  ) as T);

  const [rosterIdsToInvalidateQueries, setRosterIdsToInvalidateQueries]
    = useState<number[]>([]);

  const getCurrentRoute = () => {
    return (multipleRoutesRendered && router.state.matches
      .find((match) => match.pathname === router.state.location.pathname)?.route.path
    ) || router.state.location.pathname;
  };

  const previousRoute = getCurrentRoute();

  useEffect(() => {
    return () => {
      // setTimeout is required to not invalidate queries right before exiting page,
      // but rather when re-entering the page again
      setTimeout(() => {
        if (previousRoute === getCurrentRoute()) {
          return;
        }

        if (invalidateOnUnMount) {
          invalidate();
          return;
        }

        for (const rosterId of rosterIdsToInvalidateQueries) {
          rosterIdRemoveInvalidateQuery(rosterId);
          invalidate(rosterId);
        }
      });
    };
  }, [router.state.location.pathname, rosterIdsToInvalidateQueries]);

  const rosterIdInvalidateQuery = (rosterId: number) => {
    setRosterIdsToInvalidateQueries(Array.from(new Set([...rosterIdsToInvalidateQueries, rosterId])));
  };

  const rosterIdRemoveInvalidateQuery = (rosterId: number) => {
    setRosterIdsToInvalidateQueries(
      [...rosterIdsToInvalidateQueries.filter((id) => id !== rosterId)]
    );
  };

  const queryFnCallback: QueryFnCallback = (forceInvalidate) => {
    const reasonToInvalidate = !isEqual(params, currentRosterParams);

    if (forceInvalidate || reasonToInvalidate) {
      rosterIdInvalidateQuery(currentRosterId);
    } else {
      rosterIdRemoveInvalidateQuery(currentRosterId);
    }

    return Object.values(currentRosterParams)
      .some((param) => !Helpers.isEmptyUnknown(param));
  };

  return {
    params: currentRosterParams,
    queryFnCallback,
  };
}
